/**
 * facilitiesView.ts - 3x4 grid map for facilities
 *
 * Renders a 12-plot grid (4 rows x 3 columns).
 * Empty plots show dashed borders with "+" sign.
 * Locked plots show dark dashed borders with lock icon.
 * Built plots show facility image with info overlay.
 */

import type { GameStateData, FacilityPlot } from '../game/state.js';
import { GHOST_DEFS } from '../game/data/ghosts.js';
import { FACILITY_DEFS, ALL_FACILITY_IDS, facilityCostForLevel } from '../game/data/facilities.js';
import type { FacilityId } from '../game/data/facilities.js';
import type { FacilityPropId } from '../game/data/facilityProps.js';
import { plotUnlockCost } from '../game/commands.js';
import { HAUNTED_THEMES } from '../game/data/themes.js';
import type { HauntedThemeId } from '../game/state.js';
import { t } from '../i18n/index.js';
import { formatSp } from './formatters.js';

/** Facility background images */
const FACILITY_BG_IMAGES: Record<string, string> = {
  haunted_house: '/images/views/facility-hauntedhouse-bg.webp',
  ticket_booth: '/images/views/facility-ticketbooth-bg.webp',
  gift_shop: '/images/views/facility-giftshop-bg.webp',
  snack_bar: '/images/views/facility-snackbar-bg.webp',
  photo_booth: '/images/views/facility-photobooth-bg.webp',
  restroom: '/images/views/facility-restroom-bg.webp',
  parking_lot: '/images/views/facility-parking-bg.webp',
  first_aid: '/images/views/facility-firstaid-bg.webp',
  arcade: '/images/views/facility-arcade-bg.webp',
  fortune_teller: '/images/views/facility-fortuneteller-bg.webp',
  escape_room: '/images/views/facility-escaperoom-bg.webp',
  stage: '/images/views/facility-stage-bg.webp',
  voodoo_shop: '/images/views/facility-voodooshop-bg.webp',
  ghost_cafe: '/images/views/facility-ghostcafe-bg.webp',
  phantom_cinema: '/images/views/facility-phantomcinema-bg.webp',
  skeleton_spa: '/images/views/facility-skeletonspa-bg.webp',
};

// ── Action Types ───────────────────────────────────────────

export type FacilitiesViewAction =
  | { action: 'build_on_plot'; plotId: number; facilityType: FacilityId }
  | { action: 'upgrade_plot'; plotId: number }
  | { action: 'assign_plot_staff'; plotId: number; ghostInstanceId: string }
  | { action: 'unassign_plot_staff'; plotId: number }
  | { action: 'unlock_plot'; plotId: number }
  | { action: 'set_price'; plotId: number; price: number }
  | { action: 'demolish'; plotId: number }
  | { action: 'move_facility'; fromPlotId: number; toPlotId: number }
  | { action: 'toggle_facility_open'; plotId: number }
  | { action: 'build_haunted_house_on_plot'; plotId: number; themeId: string }
  | { action: 'enter_haunted_house'; buildingId: string }
  | { action: 'open_park' }
  | { action: 'ticket_booth_info'; plotId: number }
  // Legacy actions kept for backward compatibility
  | { action: 'build_floor'; facilityId: FacilityId }
  | { action: 'upgrade_floor'; level: number }
  | { action: 'assign_ghost'; floorLevel: number; ghostInstanceId: string }
  | { action: 'unassign_ghost'; floorLevel: number }
  | { action: 'buy_prop'; floorLevel: number; propId: FacilityPropId }
  | { action: 'build'; facilityId: FacilityId }
  | { action: 'upgrade'; facilityId: FacilityId }
  | { action: 'assign_staff'; facilityId: FacilityId; ghostInstanceId: string }
  | { action: 'unassign_staff'; ghostInstanceId: string };

export type FacilitiesViewCallback = (ev: FacilitiesViewAction) => void;

// ── Main Render ────────────────────────────────────────────

let _prevFacilityMapRef: GameStateData['facilityMap'] | null = null;
let _prevStreetVisitorsLen = -1;
let _prevSp = -1;
let _prevDayPhase = '';

export function renderFacilitiesView(
  container: HTMLElement,
  state: GameStateData,
  onAction: FacilitiesViewCallback,
): void {
  // Skip re-render if facilityMap, street visitor count, SP, and dayPhase haven't changed
  const svLen = state.streetVisitors?.length ?? 0;
  if (
    _prevFacilityMapRef === state.facilityMap &&
    _prevStreetVisitorsLen === svLen &&
    _prevSp === state.sp &&
    _prevDayPhase === state.dayPhase &&
    container.childElementCount > 0
  ) {
    return;
  }
  _prevFacilityMapRef = state.facilityMap;
  _prevStreetVisitorsLen = svLen;
  _prevSp = state.sp;
  _prevDayPhase = state.dayPhase;

  container.innerHTML = '';

  const wrapper = document.createElement('div');
  wrapper.className = 'fac-grid-wrapper';

  // Title
  const title = document.createElement('div');
  title.className = 'fac-grid-title';
  title.textContent = t('facility_map_title');
  wrapper.appendChild(title);

  // 8 rows x 2 columns with center road
  const grid = document.createElement('div');
  grid.className = 'fac-grid';

  const totalPlots = 16;
  const rows = totalPlots / 2; // 8 rows

  for (let row = 0; row < rows; row++) {
    const rowEl = document.createElement('div');
    rowEl.className = 'fac-row';

    // Left plot (even index: 0, 2, 4, ...)
    const leftIdx = row * 2;
    const leftPlot = state.facilityMap[leftIdx];
    if (leftPlot) {
      rowEl.appendChild(renderPlot(leftPlot, state, onAction));
    } else {
      const el = document.createElement('div');
      el.className = 'fac-plot locked';
      rowEl.appendChild(el);
    }

    // Center road
    const road = document.createElement('div');
    road.className = 'fac-path';
    rowEl.appendChild(road);

    // Right plot (odd index: 1, 3, 5, ...)
    const rightIdx = row * 2 + 1;
    const rightPlot = state.facilityMap[rightIdx];
    if (rightPlot) {
      rowEl.appendChild(renderPlot(rightPlot, state, onAction));
    } else {
      const el = document.createElement('div');
      el.className = 'fac-plot locked';
      rowEl.appendChild(el);
    }

    grid.appendChild(rowEl);
  }

  wrapper.appendChild(grid);

  // Street visitor tokens overlay (render max 40 tokens, logic tracks all)
  const MAX_RENDERED_TOKENS = 40;
  if (state.streetVisitors && state.streetVisitors.length > 0) {
    const visitorsLayer = document.createElement('div');
    visitorsLayer.className = 'street-visitors-layer';

    const visibleVisitors = state.streetVisitors.slice(0, MAX_RENDERED_TOKENS);
    for (const visitor of visibleVisitors) {
      const token = document.createElement('div');
      token.className = `street-visitor-token sv-state-${visitor.state} sv-type-${visitor.visitorType || 'thrill_seeker'}`;
      // Position: Y maps to vertical position within grid
      const topPct = Math.max(2, Math.min(96, visitor.posY * 100));
      // X: position based on target facility column or random walk
      const idHash = visitor.id.charCodeAt(visitor.id.length - 1);
      const spread = (idHash % 16) - 8; // ±8% random spread
      let leftPct: number;
      if (visitor.state === 'using' && visitor.targetPlotId >= 0) {
        // At facility: left column (even plotId) → ~25%, right column (odd) → ~75%
        leftPct = (visitor.targetPlotId % 2 === 0) ? 22 + (spread * 0.5) : 78 + (spread * 0.5);
      } else if (visitor.state === 'walking' && visitor.targetPlotId >= 0) {
        // Walking toward facility: interpolate from center toward target column
        const targetX = (visitor.targetPlotId % 2 === 0) ? 25 : 75;
        leftPct = 50 + (targetX - 50) * 0.6 + spread;
      } else {
        // Browsing/entering/exiting: spread across center road area (30%-70%)
        leftPct = 30 + (idHash % 40) + (spread * 0.3);
      }
      leftPct = Math.max(5, Math.min(95, leftPct));
      token.style.top = `${topPct}%`;
      token.style.left = `${leftPct}%`;

      // Type indicator (tiny colored dot)
      const typeIcon = document.createElement('div');
      typeIcon.className = 'sv-type-icon';
      typeIcon.textContent = VISITOR_TYPE_ICONS[visitor.visitorType || 'thrill_seeker'] || '?';
      token.appendChild(typeIcon);

      // Avatar face (coin image)
      const face = document.createElement('img');
      face.className = 'sv-face';
      face.src = getVisitorCoinImage(visitor.visitorType || 'thrill_seeker', visitor.avatarIdx);
      face.alt = '';
      face.draggable = false;
      token.appendChild(face);

      // Satisfaction speech bubble (shows after using facility)
      if (visitor.state === 'walking' && visitor.totalSpent > 0) {
        if (visitor.satisfaction >= 80) {
          const bubble = document.createElement('div');
          bubble.className = 'sv-mood-bubble mood-happy';
          bubble.textContent = '+';
          token.appendChild(bubble);
        } else if (visitor.satisfaction < 40) {
          const bubble = document.createElement('div');
          bubble.className = 'sv-mood-bubble mood-unhappy';
          bubble.textContent = '-';
          token.appendChild(bubble);
        }
      }

      // Need indicator (highest need as tiny icon)
      if (visitor.state === 'walking' && visitor.targetPlotId >= 0) {
        const indicator = document.createElement('div');
        indicator.className = 'sv-need-indicator';
        const plot = state.facilityMap.find(p => p.id === visitor.targetPlotId);
        if (plot?.facility) {
          indicator.textContent = FACILITY_ICONS[plot.facility.type] || '?';
        }
        token.appendChild(indicator);
      }

      // Spending animation
      if (visitor.state === 'using') {
        const spBubble = document.createElement('div');
        spBubble.className = 'sv-sp-bubble';
        spBubble.textContent = t('sp');
        token.appendChild(spBubble);
      }

      visitorsLayer.appendChild(token);
    }

    // Visitor dashboard (count + type breakdown + WoM + season)
    const dashboard = document.createElement('div');
    dashboard.className = 'sv-dashboard';

    const total = state.streetVisitors.length;
    const thrillCount = state.streetVisitors.filter(v => v.visitorType === 'thrill_seeker').length;
    const familyCount = state.streetVisitors.filter(v => v.visitorType === 'family').length;
    const coupleCount = state.streetVisitors.filter(v => v.visitorType === 'couple').length;
    const vipCount = state.streetVisitors.filter(v => v.visitorType === 'vip').length;

    // Total count
    const countLine = document.createElement('div');
    countLine.className = 'sv-dash-count';
    countLine.textContent = `${total}`;
    dashboard.appendChild(countLine);

    // Type breakdown (colored dots)
    const typeLine = document.createElement('div');
    typeLine.className = 'sv-dash-types';
    const typeEntries: [string, string, number][] = [
      ['sv-dt-thrill', '!', thrillCount],
      ['sv-dt-family', 'F', familyCount],
      ['sv-dt-couple', 'C', coupleCount],
    ];
    if (vipCount > 0) typeEntries.push(['sv-dt-vip', 'V', vipCount]);
    for (const [cls, prefix, count] of typeEntries) {
      const span = document.createElement('span');
      span.className = cls;
      span.textContent = `${prefix}${count}`;
      typeLine.appendChild(span);
      typeLine.appendChild(document.createTextNode(' '));
    }
    dashboard.appendChild(typeLine);

    // Word-of-mouth indicator
    const wom = state.wordOfMouth ?? 1.0;
    const womLine = document.createElement('div');
    womLine.className = 'sv-dash-wom';
    const womArrow = wom > 1.1 ? '+' : wom < 0.9 ? '-' : '=';
    womLine.textContent = `${womArrow}${Math.round(wom * 100)}%`;
    if (wom > 1.1) womLine.classList.add('wom-up');
    else if (wom < 0.9) womLine.classList.add('wom-down');
    dashboard.appendChild(womLine);

    // Season event badge
    if (state.seasonEvent) {
      const seasonBadge = document.createElement('div');
      seasonBadge.className = `sv-dash-season season-${state.seasonEvent.id}`;
      seasonBadge.textContent = t(`season_${state.seasonEvent.id}` as Parameters<typeof t>[0]);
      dashboard.appendChild(seasonBadge);
    }

    visitorsLayer.appendChild(dashboard);

    wrapper.appendChild(visitorsLayer);
  }

  container.appendChild(wrapper);
}

const VISITOR_TYPE_ICONS: Record<string, string> = {
  thrill_seeker: '!',
  family: 'F',
  couple: 'C',
  vip: 'V',
};

// Coin avatar images — chibi pixel art characters in visitor/ folder
const VISITOR_COIN_IMAGES = [
  '/images/visitor/coin-visitor-1.png',
  '/images/visitor/coin-visitor-2.png',
  '/images/visitor/coin-visitor-3.png',
];

function getVisitorCoinImage(_visitorType: string, avatarIdx: number): string {
  return VISITOR_COIN_IMAGES[avatarIdx % VISITOR_COIN_IMAGES.length];
}

const FACILITY_ICONS: Record<string, string> = {
  haunted_house: '!',
  gift_shop: '$',
  snack_bar: '~',
  restroom: 'W',
  photo_booth: '#',
  parking_lot: 'P',
  first_aid: '+',
};

// ── Plot Rendering ─────────────────────────────────────────

function renderPlot(
  plot: FacilityPlot,
  state: GameStateData,
  onAction: FacilitiesViewCallback,
): HTMLElement {
  const el = document.createElement('div');
  el.className = 'fac-plot';

  if (!plot.unlocked) {
    // Locked plot
    el.classList.add('locked');
    const lockIcon = document.createElement('div');
    lockIcon.className = 'fac-plot-lock-icon';
    lockIcon.textContent = '?';
    el.appendChild(lockIcon);

    const costLabel = document.createElement('div');
    costLabel.className = 'fac-plot-lock-cost';
    costLabel.textContent = t('facility_unlock_plot', { sp: formatSp(plotUnlockCost(plot.id)) });
    el.appendChild(costLabel);

    el.addEventListener('click', () => {
      showUnlockPlotPopup(plot, state, onAction);
    });
  } else if (!plot.facility) {
    // Empty unlocked plot
    el.classList.add('empty');
    const plusIcon = document.createElement('div');
    plusIcon.className = 'plus-icon';
    plusIcon.textContent = '+';
    el.appendChild(plusIcon);

    const buildLabel = document.createElement('div');
    buildLabel.className = 'fac-plot-build-label';
    buildLabel.textContent = t('facility_build_here');
    el.appendChild(buildLabel);

    el.addEventListener('click', () => {
      showBuildFacilityPopup(plot, state, onAction);
    });
  } else {
    // Built facility
    el.classList.add('built');
    if (plot.facility.isOpen === false) {
      el.classList.add('facility-closed');
    }

    const facilityDef = FACILITY_DEFS[plot.facility.type];
    if (!facilityDef) return el;

    // Closed badge overlay
    if (plot.facility.isOpen === false) {
      const closedBadge = document.createElement('div');
      closedBadge.className = 'fac-plot-closed-badge';
      closedBadge.textContent = t('facility_closed_badge');
      el.appendChild(closedBadge);
    }

    // Special handling for haunted_house facility (building entrance)
    if (plot.facility.type === 'haunted_house') {
      el.classList.add('haunted-house-plot');

      // Look up building meta for this specific plot's buildingId
      const buildingId = plot.facility.buildingId || 'building_1';
      const buildingMeta = state.hauntedBuildingMetas?.find(m => m.id === buildingId);

      const bgImg = document.createElement('img');
      bgImg.className = 'fac-plot-img';
      bgImg.src = (buildingMeta?.buildingExteriorAsset && buildingMeta.buildingExteriorAsset.length > 0)
        ? buildingMeta.buildingExteriorAsset
        : '/images/views/facility-hauntedhouse-bg.webp';
      bgImg.alt = '';
      bgImg.draggable = false;
      el.appendChild(bgImg);

      const enterHint = document.createElement('div');
      enterHint.className = 'fac-plot-enter-hint';
      enterHint.textContent = t('facility_enter');
      el.appendChild(enterHint);

      el.addEventListener('click', () => {
        onAction({ action: 'enter_haunted_house', buildingId });
      });

      return el;
    }

    // Special handling for ticket_booth (park entrance)
    if (plot.facility.type === 'ticket_booth') {
      el.classList.add('ticket-booth-plot');

      const bgImg = document.createElement('img');
      bgImg.className = 'fac-plot-img';
      bgImg.src = FACILITY_BG_IMAGES['ticket_booth'] || '/images/views/facility-ticketbooth-bg.webp';
      bgImg.alt = '';
      bgImg.draggable = false;
      el.appendChild(bgImg);

      const nameEl = document.createElement('div');
      nameEl.className = 'fac-plot-name';
      nameEl.textContent = t('facility_ticket_booth');
      el.appendChild(nameEl);

      // Show open/closed status + visitor count
      const isDayPhase = state.dayPhase === 'day';
      if (isDayPhase) {
        const statusEl = document.createElement('div');
        statusEl.className = 'fac-plot-enter-hint ticket-booth-closed';
        statusEl.textContent = t('toggle_open');
        el.appendChild(statusEl);
      } else {
        // Visitor counter badge during business hours — sum across all buildings
        const streetCount = state.streetVisitors?.length ?? 0;
        let totalQueueCount = 0;
        let totalMaxQueue = 0;
        if (state.hauntedBuildings && state.hauntedBuildings.length > 0) {
          for (const bld of state.hauntedBuildings) {
            totalQueueCount += (bld.lobbyWaitQueue || []).length;
            totalMaxQueue += (bld.maxQueueSize || 10);
          }
        } else {
          totalQueueCount = state.lobbyWaitQueue?.length ?? 0;
          totalMaxQueue = state.maxQueueSize || 10;
        }
        const totalVisitors = streetCount + totalQueueCount;
        const counterEl = document.createElement('div');
        counterEl.className = 'ticket-booth-counter';
        const queueClass = totalQueueCount >= totalMaxQueue ? 'tb-queue-full' : '';
        counterEl.innerHTML = `<span class="tb-count">${totalVisitors}</span><span class="tb-label">${t('facility_ticket_booth_visitors')}</span><span class="tb-queue ${queueClass}">${totalQueueCount}/${totalMaxQueue}</span>`;
        el.appendChild(counterEl);
      }

      el.addEventListener('click', () => {
        if (isDayPhase) {
          onAction({ action: 'open_park' });
        } else {
          onAction({ action: 'ticket_booth_info', plotId: plot.id });
        }
      });

      return el;
    }

    // Background image
    const bgSrc = FACILITY_BG_IMAGES[plot.facility.type];
    if (bgSrc) {
      const bgImg = document.createElement('img');
      bgImg.className = 'fac-plot-img';
      bgImg.src = bgSrc;
      bgImg.alt = '';
      bgImg.draggable = false;
      el.appendChild(bgImg);
    }

    // Name label at bottom
    const nameEl = document.createElement('div');
    nameEl.className = 'fac-plot-name';
    nameEl.textContent = t(facilityDef.nameKey as Parameters<typeof t>[0]);
    el.appendChild(nameEl);

    // Level badge (top-right)
    if (plot.facility.upgradeLevel > 1) {
      const lvBadge = document.createElement('div');
      lvBadge.className = 'fac-plot-level';
      lvBadge.textContent = `+${plot.facility.upgradeLevel - 1}`;
      el.appendChild(lvBadge);
    }

    // Income badge (top-left)
    if (facilityDef.spPerTick > 0) {
      const income = document.createElement('div');
      income.className = 'fac-plot-income';
      income.textContent = `${facilityDef.spPerTick * plot.facility.upgradeLevel} SP`;
      el.appendChild(income);
    }

    // Staff avatar
    if (plot.facility.assignedGhostId) {
      const ghost = state.ghosts.find((g) => g.id === plot.facility!.assignedGhostId);
      if (ghost) {
        const ghostDef = GHOST_DEFS[ghost.characterId];
        if (ghostDef) {
          const staffEl = document.createElement('div');
          staffEl.className = 'fac-plot-staff';
          const avatar = document.createElement('img');
          avatar.className = 'fac-plot-staff-avatar';
          avatar.src = ghostDef.image;
          avatar.alt = '';
          avatar.draggable = false;
          staffEl.appendChild(avatar);
          el.appendChild(staffEl);
        }
      }
    }

    el.addEventListener('click', () => {
      showFacilityInfoPopup(plot, state, onAction);
    });
  }

  return el;
}

// ── Popup: Unlock Plot ─────────────────────────────────────

function showUnlockPlotPopup(
  plot: FacilityPlot,
  state: GameStateData,
  onAction: FacilitiesViewCallback,
): void {
  removeExistingPopup();

  const cost = plotUnlockCost(plot.id);
  const canAfford = state.sp >= cost;

  const overlay = createPopupOverlay();
  const box = createPopupBox();

  const titleEl = document.createElement('div');
  titleEl.className = 'popup-title';
  titleEl.textContent = t('facility_unlock_plot_title');
  box.appendChild(titleEl);

  const desc = document.createElement('div');
  desc.className = 'popup-desc';
  desc.textContent = t('facility_unlock_plot_desc', { sp: formatSp(cost) });
  box.appendChild(desc);

  const btnRow = document.createElement('div');
  btnRow.className = 'popup-btn-row';

  const cancelBtn = document.createElement('button');
  cancelBtn.className = 'popup-btn secondary';
  cancelBtn.textContent = t('cancel');
  cancelBtn.addEventListener('click', () => overlay.remove());
  btnRow.appendChild(cancelBtn);

  const unlockBtn = document.createElement('button');
  unlockBtn.className = 'popup-btn primary';
  unlockBtn.textContent = t('facility_unlock_plot_btn', { sp: formatSp(cost) });
  unlockBtn.disabled = !canAfford;
  unlockBtn.addEventListener('click', () => {
    onAction({ action: 'unlock_plot', plotId: plot.id });
    overlay.remove();
  });
  btnRow.appendChild(unlockBtn);

  box.appendChild(btnRow);
  overlay.appendChild(box);
  document.body.appendChild(overlay);
}

// ── Popup: Build Facility (Card-style) ─────────────────────

function showBuildFacilityPopup(
  plot: FacilityPlot,
  state: GameStateData,
  onAction: FacilitiesViewCallback,
): void {
  removeExistingPopup();

  // Show all facility types - mark already-built ones with badge
  const builtTypes = new Set(
    state.facilityMap.filter((p) => p.facility).map((p) => p.facility!.type),
  );

  const overlay = createPopupOverlay();
  const box = createPopupBox();
  box.classList.add('popup-box-large');

  const titleEl = document.createElement('div');
  titleEl.className = 'popup-title';
  titleEl.textContent = t('facility_build_popup_title');
  box.appendChild(titleEl);

  const list = document.createElement('div');
  list.className = 'fac-build-cards';

  // haunted_house and ticket_booth are default fixed facilities, never shown in buildable list
  const buildableFacilities = ALL_FACILITY_IDS.filter(id => id !== 'haunted_house' && id !== 'ticket_booth');

  for (const facilityId of buildableFacilities) {
    const def = FACILITY_DEFS[facilityId];
    const canAfford = state.sp >= def.buildCost;
    const alreadyBuilt = builtTypes.has(facilityId);

    const card = document.createElement('div');
    card.className = 'fac-build-card';
    if (!canAfford) card.classList.add('disabled');

    // Thumbnail (large)
    const thumbWrap = document.createElement('div');
    thumbWrap.className = 'fac-build-card-thumb';
    const bgSrc = FACILITY_BG_IMAGES[facilityId];
    if (bgSrc) {
      const img = document.createElement('img');
      img.src = bgSrc;
      img.alt = '';
      img.draggable = false;
      thumbWrap.appendChild(img);
    }
    // "Built" badge
    if (alreadyBuilt) {
      const badge = document.createElement('div');
      badge.className = 'fac-build-card-badge';
      badge.textContent = t('facility_built');
      thumbWrap.appendChild(badge);
    }
    card.appendChild(thumbWrap);

    // Info
    const info = document.createElement('div');
    info.className = 'fac-build-card-info';

    const name = document.createElement('div');
    name.className = 'fac-build-card-name';
    name.textContent = t(def.nameKey as Parameters<typeof t>[0]);
    info.appendChild(name);

    const desc = document.createElement('div');
    desc.className = 'fac-build-card-desc';
    desc.textContent = t(def.descKey as Parameters<typeof t>[0]);
    info.appendChild(desc);

    const bonus = document.createElement('div');
    bonus.className = 'fac-build-card-bonus';
    bonus.textContent = t(def.bonusEffect as Parameters<typeof t>[0]);
    info.appendChild(bonus);

    const costEl = document.createElement('div');
    costEl.className = 'fac-build-card-cost';
    costEl.textContent = `${formatSp(def.buildCost)} SP`;
    info.appendChild(costEl);

    card.appendChild(info);

    // Build button
    const buildBtn = document.createElement('button');
    buildBtn.className = 'fac-build-card-btn';
    buildBtn.disabled = !canAfford;
    buildBtn.textContent = t('facility_build_action');
    buildBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (!canAfford) return;
      onAction({ action: 'build_on_plot', plotId: plot.id, facilityType: facilityId });
      overlay.remove();
    });
    card.appendChild(buildBtn);

    list.appendChild(card);
  }

  box.appendChild(list);

  const closeBtn = document.createElement('button');
  closeBtn.className = 'popup-btn secondary popup-close-btn';
  closeBtn.textContent = t('close');
  closeBtn.addEventListener('click', () => overlay.remove());
  box.appendChild(closeBtn);

  overlay.appendChild(box);
  document.body.appendChild(overlay);
}

// ── Popup: Facility Info (Detail) ──────────────────────────

export function showFacilityInfoPopup(
  plot: FacilityPlot,
  state: GameStateData,
  onAction: FacilitiesViewCallback,
): void {
  if (!plot.facility) return;
  removeExistingPopup();

  const fac = plot.facility;
  const def = FACILITY_DEFS[fac.type];
  if (!def) return;

  const overlay = createPopupOverlay();
  const box = createPopupBox();
  box.classList.add('popup-box-large', 'fac-info-popup');

  // Header: image + name
  const header = document.createElement('div');
  header.className = 'fac-info-header';

  const headerImg = document.createElement('div');
  headerImg.className = 'fac-info-header-img';
  const bgSrc = FACILITY_BG_IMAGES[fac.type];
  if (bgSrc) {
    const img = document.createElement('img');
    img.src = bgSrc;
    img.alt = '';
    img.draggable = false;
    headerImg.appendChild(img);
  }
  // Level badge on image
  if (fac.upgradeLevel > 1) {
    const lvBadge = document.createElement('div');
    lvBadge.className = 'fac-info-level-badge';
    lvBadge.textContent = `+${fac.upgradeLevel - 1}`;
    headerImg.appendChild(lvBadge);
  }
  header.appendChild(headerImg);

  const headerText = document.createElement('div');
  headerText.className = 'fac-info-header-text';
  const nameEl = document.createElement('div');
  nameEl.className = 'fac-info-name';
  nameEl.textContent = t(def.nameKey as Parameters<typeof t>[0]);
  headerText.appendChild(nameEl);
  const descEl = document.createElement('div');
  descEl.className = 'fac-info-desc';
  descEl.textContent = t(def.descKey as Parameters<typeof t>[0]);
  headerText.appendChild(descEl);

  const bonusEl = document.createElement('div');
  bonusEl.className = 'fac-info-bonus';
  bonusEl.textContent = t(def.bonusEffect as Parameters<typeof t>[0]);
  headerText.appendChild(bonusEl);

  // Rating display
  const ratingCount = fac.totalRatings || 0;
  if (ratingCount > 0) {
    const avgRating = (fac.ratingSum || 0) / ratingCount;
    const ratingEl = document.createElement('div');
    ratingEl.className = 'fac-info-rating';
    const fullStars = Math.floor(avgRating);
    const halfStar = avgRating - fullStars >= 0.5;
    let starsHtml = '';
    for (let i = 0; i < 5; i++) {
      if (i < fullStars) starsHtml += '<span class="fac-star filled">&#9733;</span>';
      else if (i === fullStars && halfStar) starsHtml += '<span class="fac-star half">&#9733;</span>';
      else starsHtml += '<span class="fac-star empty">&#9734;</span>';
    }
    ratingEl.innerHTML = `${starsHtml} <span class="fac-rating-text">${avgRating.toFixed(1)} (${ratingCount})</span>`;
    headerText.appendChild(ratingEl);
  }

  header.appendChild(headerText);

  box.appendChild(header);

  // Revenue section
  const revenueSection = document.createElement('div');
  revenueSection.className = 'fac-info-section';

  const revenueTitle = document.createElement('div');
  revenueTitle.className = 'fac-info-section-title';
  revenueTitle.textContent = t('facility_revenue');
  revenueSection.appendChild(revenueTitle);

  const revenueRow = document.createElement('div');
  revenueRow.className = 'fac-info-revenue-row';

  const totalRev = document.createElement('div');
  totalRev.className = 'fac-info-revenue-item';
  const totalRevLabel = document.createElement('div');
  totalRevLabel.className = 'fac-info-revenue-label';
  totalRevLabel.textContent = t('facility_total_revenue');
  totalRev.appendChild(totalRevLabel);
  const totalRevValue = document.createElement('div');
  totalRevValue.className = 'fac-info-revenue-value';
  totalRevValue.textContent = `${formatSp(fac.totalRevenue)} SP`;
  totalRev.appendChild(totalRevValue);
  revenueRow.appendChild(totalRev);

  const hourlyRev = document.createElement('div');
  hourlyRev.className = 'fac-info-revenue-item';
  const hourlyRevLabel = document.createElement('div');
  hourlyRevLabel.className = 'fac-info-revenue-label';
  hourlyRevLabel.textContent = t('facility_hourly_revenue');
  hourlyRev.appendChild(hourlyRevLabel);
  const hourlyRevValue = document.createElement('div');
  hourlyRevValue.className = 'fac-info-revenue-value';
  const hourlyIncome = def.spPerTick * fac.upgradeLevel;
  hourlyRevValue.textContent = hourlyIncome > 0
    ? `${hourlyIncome} SP / ${t('facility_detail_sp_per_group', { n: hourlyIncome })}`
    : t('facility_detail_no_income');
  hourlyRev.appendChild(hourlyRevValue);
  revenueRow.appendChild(hourlyRev);

  revenueSection.appendChild(revenueRow);
  box.appendChild(revenueSection);

  // Price setting section
  const priceSection = document.createElement('div');
  priceSection.className = 'fac-info-section';

  const priceTitle = document.createElement('div');
  priceTitle.className = 'fac-info-section-title';
  priceTitle.textContent = t('facility_set_price');
  priceSection.appendChild(priceTitle);

  let currentPrice = fac.price ?? 0;

  const priceControl = document.createElement('div');
  priceControl.className = 'fac-info-price-control';

  const minusBtn = document.createElement('button');
  minusBtn.className = 'fac-price-btn';
  minusBtn.textContent = '-';
  priceControl.appendChild(minusBtn);

  const priceDisplay = document.createElement('div');
  priceDisplay.className = 'fac-price-display';
  priceDisplay.textContent = `${currentPrice} SP`;
  priceControl.appendChild(priceDisplay);

  const plusBtn = document.createElement('button');
  plusBtn.className = 'fac-price-btn';
  plusBtn.textContent = '+';
  priceControl.appendChild(plusBtn);

  priceSection.appendChild(priceControl);

  // Price hint
  const priceHint = document.createElement('div');
  priceHint.className = 'fac-price-hint';
  function updatePriceHint(): void {
    if (currentPrice > (def.spPerTick * fac.upgradeLevel * 3)) {
      priceHint.textContent = t('facility_price_hint_high');
      priceHint.className = 'fac-price-hint hint-high';
    } else if (currentPrice === 0) {
      priceHint.textContent = t('facility_price_hint_free');
      priceHint.className = 'fac-price-hint hint-low';
    } else {
      priceHint.textContent = t('facility_price_hint_normal');
      priceHint.className = 'fac-price-hint hint-normal';
    }
  }
  updatePriceHint();
  priceSection.appendChild(priceHint);

  minusBtn.addEventListener('click', () => {
    if (currentPrice > 0) {
      currentPrice = Math.max(0, currentPrice - 1);
      priceDisplay.textContent = `${currentPrice} SP`;
      updatePriceHint();
      onAction({ action: 'set_price', plotId: plot.id, price: currentPrice });
    }
  });

  plusBtn.addEventListener('click', () => {
    currentPrice += 1;
    priceDisplay.textContent = `${currentPrice} SP`;
    updatePriceHint();
    onAction({ action: 'set_price', plotId: plot.id, price: currentPrice });
  });

  box.appendChild(priceSection);

  // Staff section
  const staffSection = document.createElement('div');
  staffSection.className = 'fac-info-section';

  const staffTitle = document.createElement('div');
  staffTitle.className = 'fac-info-section-title';
  staffTitle.textContent = t('facility_staff');
  staffSection.appendChild(staffTitle);

  if (fac.assignedGhostId) {
    const ghost = state.ghosts.find((g) => g.id === fac.assignedGhostId);
    if (ghost) {
      const ghostDef = GHOST_DEFS[ghost.characterId];
      if (ghostDef) {
        const staffInfo = document.createElement('div');
        staffInfo.className = 'fac-info-staff-row';

        const avatar = document.createElement('img');
        avatar.className = 'fac-info-staff-avatar';
        avatar.src = ghostDef.image;
        avatar.alt = t(ghostDef.nameKey as Parameters<typeof t>[0]);
        avatar.draggable = false;
        staffInfo.appendChild(avatar);

        const staffName = document.createElement('span');
        staffName.className = 'fac-info-staff-name';
        staffName.textContent = `${t(ghostDef.nameKey as Parameters<typeof t>[0])} Lv.${ghost.level}`;
        staffInfo.appendChild(staffName);

        const unassignBtn = document.createElement('button');
        unassignBtn.className = 'fac-info-staff-btn secondary';
        unassignBtn.textContent = t('facility_unassign_staff');
        unassignBtn.addEventListener('click', () => {
          onAction({ action: 'unassign_plot_staff', plotId: plot.id });
          overlay.remove();
        });
        staffInfo.appendChild(unassignBtn);

        staffSection.appendChild(staffInfo);
      }
    }
  } else {
    const noStaff = document.createElement('div');
    noStaff.className = 'fac-info-no-staff';
    noStaff.textContent = t('facility_staff_none');
    staffSection.appendChild(noStaff);

    // Available ghosts for assignment
    const available = state.ghosts.filter((g) => {
      if (g.assignedRoomId || g.training || g.academySession || g.assignedFacilityId) return false;
      if (state.lobbyStaff.includes(g.id)) return false;
      if (state.lobbyBuilding.some((f) => f.assignedGhostId === g.id)) return false;
      if (state.facilityBuilding.some((f) => f.assignedGhostId === g.id)) return false;
      if (state.facilityMap.some((p) => p.facility?.assignedGhostId === g.id)) return false;
      return true;
    });

    if (available.length > 0) {
      const select = document.createElement('select');
      select.className = 'fac-staff-select';
      const defaultOpt = document.createElement('option');
      defaultOpt.value = '';
      defaultOpt.textContent = t('facility_assign_staff');
      select.appendChild(defaultOpt);

      for (const ghost of available) {
        const ghostDef = GHOST_DEFS[ghost.characterId];
        if (!ghostDef) continue;
        const opt = document.createElement('option');
        opt.value = ghost.id;
        opt.textContent = `${t(ghostDef.nameKey as Parameters<typeof t>[0])} Lv.${ghost.level}`;
        select.appendChild(opt);
      }

      select.addEventListener('change', () => {
        if (select.value) {
          onAction({ action: 'assign_plot_staff', plotId: plot.id, ghostInstanceId: select.value });
          overlay.remove();
        }
      });
      staffSection.appendChild(select);
    }
  }

  box.appendChild(staffSection);

  // Open/Close toggle button
  const toggleOpenBtn = document.createElement('button');
  const facilityIsOpen = fac.isOpen !== false;
  const needsStaff = !facilityIsOpen && !fac.assignedGhostId && fac.type !== 'haunted_house';
  toggleOpenBtn.className = facilityIsOpen ? 'popup-btn fac-toggle-open open' : 'popup-btn fac-toggle-open closed';
  if (needsStaff) {
    toggleOpenBtn.textContent = t('facility_needs_staff');
    toggleOpenBtn.disabled = true;
  } else {
    toggleOpenBtn.textContent = facilityIsOpen ? t('facility_close') : t('facility_open');
  }
  toggleOpenBtn.addEventListener('click', () => {
    onAction({ action: 'toggle_facility_open', plotId: plot.id });
    overlay.remove();
  });
  box.appendChild(toggleOpenBtn);

  // Upgrade section
  if (fac.upgradeLevel < def.maxLevel) {
    const upgradeSection = document.createElement('div');
    upgradeSection.className = 'fac-info-section fac-upgrade-section';

    const upgTitle = document.createElement('div');
    upgTitle.className = 'fac-info-section-title';
    upgTitle.textContent = t('facility_upgrade_preview');
    upgradeSection.appendChild(upgTitle);

    // Current vs Next comparison
    const compRow = document.createElement('div');
    compRow.className = 'fac-upgrade-compare';

    const currentLevel = fac.upgradeLevel;
    const nextLevel = currentLevel + 1;
    const currentRevenue = def.spPerTick * currentLevel;
    const nextRevenue = def.spPerTick * nextLevel;
    const currentBonus = Math.round((currentLevel - 1) * 15);
    const nextBonus = Math.round((nextLevel - 1) * 15);

    // Revenue per group row
    if (def.spPerTick > 0) {
      const revRow = document.createElement('div');
      revRow.className = 'fac-upgrade-row';
      revRow.innerHTML = `<span class="fac-upgrade-label">${t('facility_upgrade_revenue')}</span>
        <span class="fac-upgrade-values"><span class="fac-upgrade-old">${currentRevenue} SP</span> → <span class="fac-upgrade-new">${nextRevenue} SP</span></span>`;
      compRow.appendChild(revRow);
    }

    // Revenue bonus row
    const bonusRow = document.createElement('div');
    bonusRow.className = 'fac-upgrade-row';
    bonusRow.innerHTML = `<span class="fac-upgrade-label">${t('facility_upgrade_bonus')}</span>
      <span class="fac-upgrade-values"><span class="fac-upgrade-old">+${currentBonus}%</span> → <span class="fac-upgrade-new">+${nextBonus}%</span></span>`;
    compRow.appendChild(bonusRow);

    // Level indicator
    const lvRow = document.createElement('div');
    lvRow.className = 'fac-upgrade-row';
    lvRow.innerHTML = `<span class="fac-upgrade-label">Lv.</span>
      <span class="fac-upgrade-values"><span class="fac-upgrade-old">${currentLevel}</span> → <span class="fac-upgrade-new">${nextLevel}</span> / ${def.maxLevel}</span>`;
    compRow.appendChild(lvRow);

    upgradeSection.appendChild(compRow);

    const cost = facilityCostForLevel(fac.type, fac.upgradeLevel + 1);
    const upgradeBtn = document.createElement('button');
    upgradeBtn.className = 'popup-btn primary fac-info-upgrade-btn';
    upgradeBtn.disabled = state.sp < cost;
    upgradeBtn.textContent = t('facility_detail_upgrade', { sp: formatSp(cost) });
    upgradeBtn.addEventListener('click', () => {
      onAction({ action: 'upgrade_plot', plotId: plot.id });
      overlay.remove();
    });
    upgradeSection.appendChild(upgradeBtn);
    box.appendChild(upgradeSection);
  } else {
    // Max level badge
    const maxBadge = document.createElement('div');
    maxBadge.className = 'fac-upgrade-max-badge';
    maxBadge.textContent = t('facility_upgrade_max_level');
    box.appendChild(maxBadge);
  }

  // Bottom action row: Move + Demolish (hidden for fixed facilities)
  const isFixedFacility = fac.type === 'haunted_house' || fac.type === 'ticket_booth';
  if (!isFixedFacility) {
    const actionRow = document.createElement('div');
    actionRow.className = 'fac-info-action-row';

    // Move button
    const moveBtn = document.createElement('button');
    moveBtn.className = 'fac-info-action-btn move';
    moveBtn.textContent = t('facility_move');
    moveBtn.addEventListener('click', () => {
      overlay.remove();
      showMoveSelectPopup(plot, state, onAction);
    });
    actionRow.appendChild(moveBtn);

    // Demolish button
    const demolishBtn = document.createElement('button');
    demolishBtn.className = 'fac-info-action-btn demolish';
    demolishBtn.textContent = t('facility_demolish');
    demolishBtn.addEventListener('click', () => {
      overlay.remove();
      showDemolishConfirmPopup(plot, state, onAction);
    });
    actionRow.appendChild(demolishBtn);

    box.appendChild(actionRow);
  }

  // Close button
  const closeBtn = document.createElement('button');
  closeBtn.className = 'popup-btn secondary popup-close-btn';
  closeBtn.textContent = t('close');
  closeBtn.addEventListener('click', () => overlay.remove());
  box.appendChild(closeBtn);

  overlay.appendChild(box);
  document.body.appendChild(overlay);
}

// ── Popup: Demolish Confirm ────────────────────────────────

function showDemolishConfirmPopup(
  plot: FacilityPlot,
  _state: GameStateData,
  onAction: FacilitiesViewCallback,
): void {
  removeExistingPopup();

  if (!plot.facility) return;
  const def = FACILITY_DEFS[plot.facility.type];
  if (!def) return;

  const overlay = createPopupOverlay();
  const box = createPopupBox();

  const titleEl = document.createElement('div');
  titleEl.className = 'popup-title';
  titleEl.textContent = t('facility_demolish');
  box.appendChild(titleEl);

  const desc = document.createElement('div');
  desc.className = 'popup-desc';
  desc.textContent = t('facility_demolish_confirm', {
    name: t(def.nameKey as Parameters<typeof t>[0]),
  });
  box.appendChild(desc);

  const btnRow = document.createElement('div');
  btnRow.className = 'popup-btn-row';

  const cancelBtn = document.createElement('button');
  cancelBtn.className = 'popup-btn secondary';
  cancelBtn.textContent = t('cancel');
  cancelBtn.addEventListener('click', () => overlay.remove());
  btnRow.appendChild(cancelBtn);

  const confirmBtn = document.createElement('button');
  confirmBtn.className = 'popup-btn demolish-confirm-btn';
  confirmBtn.textContent = t('confirm');
  confirmBtn.addEventListener('click', () => {
    onAction({ action: 'demolish', plotId: plot.id });
    overlay.remove();
  });
  btnRow.appendChild(confirmBtn);

  box.appendChild(btnRow);
  overlay.appendChild(box);
  document.body.appendChild(overlay);
}

// ── Popup: Move Select ─────────────────────────────────────

function showMoveSelectPopup(
  plot: FacilityPlot,
  state: GameStateData,
  onAction: FacilitiesViewCallback,
): void {
  removeExistingPopup();

  if (!plot.facility) return;

  const emptyPlots = state.facilityMap.filter((p) => p.unlocked && !p.facility && p.id !== plot.id);

  const overlay = createPopupOverlay();
  const box = createPopupBox();

  const titleEl = document.createElement('div');
  titleEl.className = 'popup-title';
  titleEl.textContent = t('facility_move');
  box.appendChild(titleEl);

  const desc = document.createElement('div');
  desc.className = 'popup-desc';
  desc.textContent = t('facility_move_select');
  box.appendChild(desc);

  if (emptyPlots.length === 0) {
    const emptyMsg = document.createElement('div');
    emptyMsg.className = 'popup-desc';
    emptyMsg.textContent = t('facility_move_no_empty');
    box.appendChild(emptyMsg);
  } else {
    // Show mini grid for selection
    const miniGrid = document.createElement('div');
    miniGrid.className = 'fac-move-grid';

    for (const target of emptyPlots) {
      const cell = document.createElement('div');
      cell.className = 'fac-move-cell';

      const label = document.createElement('div');
      label.className = 'fac-move-cell-label';
      label.textContent = `#${target.id + 1}`;
      cell.appendChild(label);

      cell.addEventListener('click', () => {
        onAction({ action: 'move_facility', fromPlotId: plot.id, toPlotId: target.id });
        overlay.remove();
      });
      miniGrid.appendChild(cell);
    }

    box.appendChild(miniGrid);
  }

  const closeBtn = document.createElement('button');
  closeBtn.className = 'popup-btn secondary popup-close-btn';
  closeBtn.textContent = t('cancel');
  closeBtn.addEventListener('click', () => overlay.remove());
  box.appendChild(closeBtn);

  overlay.appendChild(box);
  document.body.appendChild(overlay);
}

// ── Popup Helpers ──────────────────────────────────────────

function removeExistingPopup(): void {
  const existing = document.querySelector('.facility-popup-overlay');
  if (existing) existing.remove();
  const detailOverlay = document.querySelector('.fac-detail-overlay');
  if (detailOverlay) detailOverlay.remove();
}

function createPopupOverlay(): HTMLElement {
  const overlay = document.createElement('div');
  overlay.className = 'facility-popup-overlay';
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) overlay.remove();
  });
  return overlay;
}

function createPopupBox(): HTMLElement {
  const box = document.createElement('div');
  box.className = 'facility-popup-box';
  return box;
}

// ── Popup: Theme Selection for Haunted House ───────────────

export function showThemeSelectPopup(
  plot: FacilityPlot,
  state: GameStateData,
  onAction: FacilitiesViewCallback,
): void {
  removeExistingPopup();

  const overlay = createPopupOverlay();
  const box = createPopupBox();
  box.classList.add('popup-box-large');

  const titleEl = document.createElement('div');
  titleEl.className = 'popup-title';
  titleEl.textContent = t('facility_select_theme');
  box.appendChild(titleEl);

  // Find which themes are already in use
  const usedThemes = new Set<string>();
  for (const p of state.facilityMap) {
    if (p.facility?.type === 'haunted_house' && p.facility.buildingId) {
      const meta = state.hauntedBuildingMetas?.find(m => m.id === p.facility!.buildingId);
      if (meta?.themeId) usedThemes.add(meta.themeId);
    }
  }

  const list = document.createElement('div');
  list.className = 'fac-build-cards';

  const allThemeIds = Object.keys(HAUNTED_THEMES) as HauntedThemeId[];
  for (const themeId of allThemeIds) {
    const theme = HAUNTED_THEMES[themeId];
    const isUsed = usedThemes.has(themeId);

    const card = document.createElement('div');
    card.className = 'fac-build-card';
    if (isUsed) card.classList.add('disabled');

    const thumbWrap = document.createElement('div');
    thumbWrap.className = 'fac-build-card-thumb';
    if (theme.buildingExteriorAsset) {
      const img = document.createElement('img');
      img.src = theme.buildingExteriorAsset;
      img.alt = '';
      img.draggable = false;
      thumbWrap.appendChild(img);
    }
    if (isUsed) {
      const badge = document.createElement('div');
      badge.className = 'fac-build-card-badge';
      badge.textContent = t('facility_built');
      thumbWrap.appendChild(badge);
    }
    card.appendChild(thumbWrap);

    const info = document.createElement('div');
    info.className = 'fac-build-card-info';

    const name = document.createElement('div');
    name.className = 'fac-build-card-name';
    name.textContent = t(`theme_${themeId}` as Parameters<typeof t>[0]);
    info.appendChild(name);

    const desc = document.createElement('div');
    desc.className = 'fac-build-card-desc';
    desc.textContent = theme.description;
    info.appendChild(desc);

    card.appendChild(info);

    if (!isUsed) {
      const selectBtn = document.createElement('button');
      selectBtn.className = 'fac-build-card-btn';
      selectBtn.textContent = t('facility_build_action');
      selectBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        onAction({ action: 'build_haunted_house_on_plot', plotId: plot.id, themeId });
        overlay.remove();
      });
      card.appendChild(selectBtn);
    }

    list.appendChild(card);
  }

  box.appendChild(list);

  const closeBtn = document.createElement('button');
  closeBtn.className = 'popup-btn secondary popup-close-btn';
  closeBtn.textContent = t('close');
  closeBtn.addEventListener('click', () => overlay.remove());
  box.appendChild(closeBtn);

  overlay.appendChild(box);
  document.body.appendChild(overlay);
}
